import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { OC, FONT, CATEGORY } from '../constants/index.js';
import { useTheme } from '../constants/index.js';
import { useStore } from '../store/StoreProvider.js';
import OCApi from '../api/client.js';
import { Button, Icon, Card, Input, LogoNav } from '../components/ui.js';
import { OpportunityCard } from './browse.js';
import { LoadingState, ErrorState, EmptyState } from '../components/AsyncState.js';

function alertStatusMeta(status) {
    if (status === 'sent') return { label: 'Sent', color: OC.success };
    if (status === 'failed') return { label: 'Failed', color: OC.urgent };
    if (status === 'skipped') return { label: 'Skipped', color: OC.warning };
    return { label: status || 'Unknown', color: '#6B7280' };
}

// --- AuthScreen ---
function AuthScreen({ mode }) {
    const s = useStore();
    const t = useTheme(s.theme);
    const isSignup = mode === 'signup';
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [pw, setPw] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [registerAsEmployer, setRegisterAsEmployer] = useState(false);
    const valid = email.includes('@') && pw.length >= 6 && (!isSignup || name.trim().length > 1);

    const submit = async () => {
        if (!valid || busy) return;
        setBusy(true);
        setError('');
        try {
            if (isSignup) {
                await s.signup(email, pw, name.trim(), registerAsEmployer);
                s.go(registerAsEmployer ? 'employers' : 'feed');
            } else {
                await s.login(email, pw);
                s.go('feed');
            }
        } catch (err) {
            setError(err.message || 'Authentication failed');
        } finally {
            setBusy(false);
        }
    };

    const switchMode = (newMode) => {
        s.go(newMode === 'signup' ? 'signup' : 'login');
        window.history.pushState({}, '', `/auth?mode=${newMode}`);
    };

    return React.createElement('div', { style: { maxWidth: 480, margin: '0 auto', padding: '0 32px', width: '100%' } },
        React.createElement('div', { style: { padding: '56px 0 80px' } },
            React.createElement('div', { style: { textAlign: 'center', marginBottom: 28 } },
                React.createElement('div', { style: { display: 'inline-flex', marginBottom: 18 } },
                    React.createElement(LogoNav, { theme: s.theme, height: 30 })
                ),
                React.createElement('h1', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 28, margin: '0 0 6px', color: t.text.primary, letterSpacing: '-0.02em' } },
                    isSignup ? 'Create your account' : 'Welcome back'
                ),
                React.createElement('p', { style: { fontFamily: FONT.body, fontSize: 14.5, color: t.text.secondary, margin: 0 } },
                    isSignup ? 'Save opportunities, track applications, never miss a deadline.' : 'Log in to your circle.'
                )
            ),
            React.createElement('div', { style: { background: t.bg.card, border: `1px solid ${t.border.base}`, borderRadius: 18, padding: 26, display: 'flex', flexDirection: 'column', gap: 16 } },
                isSignup ? React.createElement(Input, { theme: s.theme, label: 'Full name', value: name, onChange: setName, placeholder: 'Aisha Khan', icon: 'user' }) : null,
                isSignup ? React.createElement('label', {
                    style: { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: t.text.secondary, cursor: 'pointer' }
                },
                    React.createElement('input', {
                        type: 'checkbox',
                        checked: registerAsEmployer,
                        onChange: e => setRegisterAsEmployer(e.target.checked),
                        style: { marginTop: 3 }
                    }),
                    React.createElement('span', null, "I'm hiring — create an employer account to post opportunities and manage applicants.")
                ) : null,
                React.createElement(Input, { theme: s.theme, label: 'Email', value: email, onChange: setEmail, placeholder: 'you@email.com', icon: 'mail', type: 'email' }),
                React.createElement(Input, {
                    theme: s.theme, label: 'Password', value: pw, onChange: setPw,
                    placeholder: 'At least 6 characters', icon: 'lock',
                    type: showPw ? 'text' : 'password',
                    rightSlot: React.createElement('button', {
                        onClick: () => setShowPw(v => !v),
                        style: { background: 'none', border: 'none', cursor: 'pointer', color: t.text.secondary, padding: 0 }
                    }, React.createElement(Icon, { name: showPw ? 'eyeOff' : 'eye', size: 17 }))
                }),
                !isSignup ? React.createElement('div', { style: { textAlign: 'right', marginTop: -6 } },
                    React.createElement('button', { onClick: () => s.go('reset'), style: { background: 'none', border: 'none', cursor: 'pointer', color: OC.orange, fontFamily: FONT.body, fontSize: 13, fontWeight: 600 } }, 'Forgot password?')
                ) : null,
                React.createElement(Button, { kind: 'primary', size: 'lg', theme: s.theme, fullWidth: true, onClick: submit, style: { opacity: valid && !busy ? 1 : 0.5, marginTop: 4 } },
                    busy ? 'Please wait…' : (isSignup ? 'Create account' : 'Log in')
                ),
                error ? React.createElement('div', { style: { color: OC.urgent, fontSize: 13, marginTop: 4 } }, error) : null
            ),
            React.createElement('div', { style: { textAlign: 'center', marginTop: 22, fontFamily: FONT.body, fontSize: 14, color: t.text.secondary } },
                isSignup ? 'Already have an account? ' : "Don't have an account? ",
                React.createElement('button', {
                    onClick: () => switchMode(isSignup ? 'login' : 'signup'),
                    style: { background: 'none', border: 'none', cursor: 'pointer', color: OC.orange, fontWeight: 700, fontFamily: FONT.body, fontSize: 14 }
                }, isSignup ? 'Log in' : 'Sign up free')
            )
        )
    );
}


// --- SearchScreen ---
function SearchScreen() {
    const s = useStore();
    const t = useTheme(s.theme);
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [remoteOnly, setRemoteOnly] = useState(false);
    const [city, setCity] = useState('');
    const [closingWeek, setClosingWeek] = useState(false);
    const suggestions = ['Remote', 'Scholarship', 'Internship', 'Design', 'Fully funded'];

    useEffect(() => {
        if (!s.query.trim()) {
            setResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            setSearching(true);
            setSearchError('');
            try {
                const params = { q: s.query.trim(), limit: 40, sort: 'relevance' };
                if (remoteOnly) params.is_remote = true;
                if (city.trim()) params.city = city.trim();
                if (closingWeek) {
                    const after = new Date();
                    const before = new Date();
                    before.setDate(before.getDate() + 7);
                    params.deadline_after = after.toISOString();
                    params.deadline_before = before.toISOString();
                }
                const data = await OCApi.getOpportunities(params);
                setResults(data.opportunities || []);
                s.loadOpportunities(params);
            } catch (err) {
                setResults([]);
                setSearchError(err.message || 'Search failed');
            } finally {
                setSearching(false);
            }
        }, 350);
        return () => clearTimeout(timer);
    }, [s.query, remoteOnly, city, closingWeek]);

    const buildSearchFilters = () => {
        const filters = { q: s.query.trim() };
        if (remoteOnly) filters.isRemote = true;
        if (city.trim()) filters.city = city.trim();
        if (closingWeek) {
            const after = new Date();
            const before = new Date();
            before.setDate(before.getDate() + 7);
            filters.deadline_after = after.toISOString();
            filters.deadline_before = before.toISOString();
        }
        return filters;
    };

    return React.createElement('div', { style: { maxWidth: 920, margin: '0 auto', padding: '0 32px', width: '100%' } },
        React.createElement('div', { style: { padding: '48px 0 80px' } },
            React.createElement('h2', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 30, margin: '0 0 20px', color: t.text.primary, letterSpacing: '-0.02em' } },
                'Search opportunities'
            ),
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 11, background: t.bg.card, border: `1.5px solid ${t.border.strong}`, borderRadius: 13, padding: '0 16px', height: 56 } },
                React.createElement(Icon, { name: 'search', size: 20, color: t.text.secondary }),
                React.createElement('input', {
                    autoFocus: true,
                    value: s.query,
                    onChange: e => s.setQuery(e.target.value),
                    placeholder: 'Try "remote design", "fully funded", "Stripe"…',
                    style: { flex: 1, border: 'none', outline: 'none', background: 'transparent', fontFamily: FONT.body, fontSize: 16, color: t.text.primary }
                }),
                s.query ? React.createElement('button', { onClick: () => s.setQuery(''), style: { background: 'none', border: 'none', cursor: 'pointer', color: t.text.secondary, padding: 8 } },
                    React.createElement(Icon, { name: 'x', size: 16 })
                ) : null
            ),
            s.query ? React.createElement('div', { style: { display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 16, marginBottom: 8 } },
                React.createElement('input', {
                    value: city,
                    onChange: e => setCity(e.target.value),
                    placeholder: 'City (e.g. Karachi)',
                    style: {
                        flex: '1 1 180px', border: `1px solid ${t.border.base}`, borderRadius: 10,
                        padding: '10px 12px', background: t.bg.page, color: t.text.primary, fontFamily: FONT.body, fontSize: 13.5,
                    }
                }),
                React.createElement('label', { style: { display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: t.text.primary } },
                    React.createElement('input', { type: 'checkbox', checked: remoteOnly, onChange: e => setRemoteOnly(e.target.checked) }),
                    'Remote only'
                ),
                React.createElement('label', { style: { display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: t.text.primary } },
                    React.createElement('input', { type: 'checkbox', checked: closingWeek, onChange: e => setClosingWeek(e.target.checked) }),
                    'Closing this week'
                )
            ) : null,
            !s.query ? React.createElement('div', { style: { marginTop: 18, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' } },
                React.createElement('span', { style: { fontFamily: FONT.body, fontSize: 13, color: t.text.secondary } }, 'Try:'),
                suggestions.map(sug =>
                    React.createElement('button', {
                        key: sug, onClick: () => s.setQuery(sug),
                        style: { border: `1px solid ${t.border.base}`, background: t.bg.card, color: t.text.primary, borderRadius: 999, padding: '7px 13px', cursor: 'pointer', fontFamily: FONT.body, fontSize: 13, fontWeight: 500 }
                    }, sug)
                )
            ) : React.createElement('div', { style: { marginTop: 24 } },
                !searching ? React.createElement('div', { style: { fontFamily: FONT.body, fontSize: 14, color: t.text.secondary, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' } },
                    React.createElement('span', null,
                        React.createElement('strong', { style: { color: t.text.primary } }, results.length),
                        ' ', results.length === 1 ? 'result' : 'results', ' for "', s.query, '"'
                    ),
                    React.createElement(Button, {
                        kind: 'secondary', size: 'sm', theme: s.theme,
                        icon: React.createElement(Icon, { name: 'bell', size: 14 }),
                        onClick: () => s.saveSearch({
                            name: `Search: ${s.query.trim().slice(0, 40)}`,
                            filters: buildSearchFilters(),
                            alertEnabled: true,
                        }),
                    }, 'Save search & alert me')
                ) : null,
                searching
                    ? React.createElement(LoadingState, { message: 'Searching…', theme: s.theme })
                    : searchError
                        ? React.createElement(ErrorState, {
                            message: searchError,
                            theme: s.theme,
                            onRetry: () => s.setQuery(`${s.query} `),
                        })
                        : results.length
                            ? React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 } },
                                results.map(op => React.createElement(OpportunityCard, { key: op.id, op: op }))
                            )
                            : React.createElement(EmptyState, {
                                theme: s.theme,
                                title: 'No results',
                                message: `Nothing matches "${s.query}". Try a broader term.`,
                                actionLabel: 'Clear search',
                                onAction: () => s.setQuery(''),
                            })
            )
        )
    );
}


// --- SavedScreen ---
function SavedScreen() {
    const s = useStore();
    const t = useTheme(s.theme);
    const [cat, setCat] = useState('all');
    const savedOps = s.saved.map(id => s.findOpportunity(id)).filter(Boolean);
    const closing = savedOps.filter(o => o.daysLeft != null && o.daysLeft <= 7);
    const cats = [...new Set(savedOps.map(o => o.category))];
    const filtered = cat === 'all' ? savedOps : cat === 'closing' ? closing : savedOps.filter(o => o.category === cat);
    const tabs = [
        ['all', 'All', savedOps.length],
        ...(closing.length ? [['closing', 'Closing soon', closing.length]] : []),
        ...cats.map(c => [c, CATEGORY[c].label, savedOps.filter(o => o.category === c).length])
    ];

    return React.createElement('div', { style: { maxWidth: 1180, margin: '0 auto', padding: '0 32px', width: '100%' } },
        React.createElement('div', { style: { padding: '40px 0 80px' } },
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap', marginBottom: 22 } },
                React.createElement('div', null,
                    React.createElement('h1', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 34, margin: 0, color: t.text.primary, letterSpacing: '-0.025em' } },
                        'Saved opportunities'
                    ),
                    React.createElement('p', { style: { fontFamily: FONT.body, fontSize: 15, color: t.text.secondary, margin: '6px 0 0' } },
                        React.createElement('strong', { style: { color: t.text.primary } }, savedOps.length),
                        ' saved',
                        closing.length ? React.createElement(React.Fragment, null, ' · ', React.createElement('span', { style: { color: OC.urgent, fontWeight: 600 } }, closing.length, ' closing this week')) : null
                    )
                ),
                React.createElement(Button, { kind: 'secondary', size: 'md', theme: s.theme, onClick: () => s.go('feed'), icon: React.createElement(Icon, { name: 'plus', size: 15 }) }, 'Browse more')
            ),
            savedOps.length ? [
                React.createElement('div', { key: 'tabs', style: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 22 } },
                    tabs.map(([k, label, n]) => {
                        const active = cat === k;
                        return React.createElement('button', {
                            key: k, onClick: () => setCat(k),
                            style: {
                                display: 'inline-flex', alignItems: 'center', gap: 7,
                                border: `1px solid ${active ? OC.orange : t.border.base}`,
                                background: active ? OC.orange : t.bg.card,
                                color: active ? '#fff' : t.text.secondary,
                                borderRadius: 999, padding: '9px 15px',
                                cursor: 'pointer', fontFamily: FONT.body,
                                fontWeight: 600, fontSize: 13.5,
                            }
                        }, label,
                            React.createElement('span', {
                                style: {
                                    minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9,
                                    background: active ? 'rgba(255,255,255,.25)' : t.bg.subtle,
                                    color: active ? '#fff' : t.text.secondary,
                                    fontSize: 11, fontWeight: 700,
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                }
                            }, n)
                        );
                    })
                ),
                React.createElement('div', { key: 'cards', style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 } },
                    filtered.map(op => React.createElement(OpportunityCard, { key: op.id, op: op }))
                )
            ] : React.createElement('div', { style: { textAlign: 'center', padding: '64px 24px', border: `1px dashed ${t.border.strong}`, borderRadius: 18, background: t.bg.card } },
                React.createElement('h3', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 19, color: t.text.primary, margin: '0 0 6px' } },
                    'No saved opportunities yet'
                ),
                React.createElement('p', { style: { fontFamily: FONT.body, fontSize: 14.5, color: t.text.secondary, margin: '0 0 20px' } },
                    'Bookmark any opportunity to keep it here and get deadline reminders.'
                ),
                React.createElement(Button, { kind: 'primary', size: 'md', theme: s.theme, onClick: () => s.go('feed') }, 'Browse opportunities')
            )
        )
    );
}


function AlertsScreen() {
    const s = useStore();
    const t = useTheme(s.theme);
    const [preview, setPreview] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);

    useEffect(() => {
        if (!s.authReady) return;
        if (!s.authed) {
            s.showToast('Log in to manage alerts', 'bell');
            s.go('login');
            return;
        }
        s.loadSavedSearchesFromApi();
    }, [s.authReady, s.authed]);

    const runPreview = async (searchId) => {
        setLoadingPreview(true);
        try {
            const data = await OCApi.previewSavedSearch(searchId, { limit: 8 });
            setPreview({
                searchId,
                summary: data.filterSummary,
                opportunities: (data.opportunities || []).map(OCApi.mapOpportunity),
            });
        } catch (err) {
            s.showToast(err.message || 'Could not load matches', 'x');
        } finally {
            setLoadingPreview(false);
        }
    };

    const cardStyle = {
        background: t.bg.card,
        border: `1px solid ${t.border.base}`,
        borderRadius: 14,
        padding: 18,
    };

    return React.createElement('div', { style: { maxWidth: 920, margin: '0 auto', padding: '0 32px', width: '100%' } },
        React.createElement('div', { style: { padding: '40px 0 80px' } },
            React.createElement('h1', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 34, margin: '0 0 6px', color: t.text.primary } },
                'Alerts & saved searches'
            ),
            React.createElement('p', { style: { color: t.text.secondary, margin: '0 0 24px' } },
                'Save a search and get emailed when new matching opportunities are published.'
            ),
            s.user.alertsPaused ? React.createElement('div', {
                style: {
                    ...cardStyle,
                    marginBottom: 20,
                    borderColor: `${OC.warning}55`,
                    background: `${OC.warning}11`,
                    color: t.text.primary,
                    fontSize: 14,
                }
            },
                'All email alerts are paused in your account settings. Saved searches will not send emails until you resume alerts.'
            ) : null,
            React.createElement('div', { style: { display: 'grid', gap: 12, marginBottom: 28 } },
                s.savedSearches.length ? s.savedSearches.map(search =>
                    React.createElement('div', {
                        key: search.id,
                        style: { ...cardStyle, display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center' }
                    },
                        React.createElement('div', null,
                            React.createElement('div', { style: { fontWeight: 700, fontSize: 16, color: t.text.primary } },
                                search.name || 'Saved search'
                            ),
                            React.createElement('div', { style: { fontSize: 13, color: t.text.secondary, marginTop: 4 } },
                                search.filterSummary || 'All opportunities'
                            ),
                            React.createElement('div', { style: { fontSize: 12, color: search.alertEnabled ? OC.success : t.text.secondary, marginTop: 6, fontWeight: 600 } },
                                search.alertEnabled ? 'Email alerts on' : 'Alerts paused'
                            )
                        ),
                        React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 8, minWidth: 160 } },
                            React.createElement(Button, {
                                kind: 'secondary', size: 'sm', theme: s.theme,
                                onClick: () => runPreview(search.id),
                                disabled: loadingPreview,
                            }, 'View matches'),
                            React.createElement(Button, {
                                kind: 'secondary', size: 'sm', theme: s.theme,
                                onClick: () => s.toggleSavedSearchAlert(search.id, !search.alertEnabled),
                            }, search.alertEnabled ? 'Pause alerts' : 'Enable alerts'),
                            React.createElement(Button, {
                                kind: 'ghost', size: 'sm', theme: s.theme,
                                onClick: () => s.removeSavedSearch(search.id),
                            }, 'Delete')
                        )
                    )
                ) : React.createElement('div', { style: { ...cardStyle, color: t.text.secondary } },
                    'No saved searches yet. Save one from Search or the Opportunities feed.'
                )
            ),
            preview ? React.createElement('div', { style: { marginBottom: 28 } },
                React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 } },
                    React.createElement('h2', { style: { fontFamily: FONT.display, fontSize: 20, margin: 0, color: t.text.primary } },
                        `Matches · ${preview.summary}`
                    ),
                    React.createElement('button', {
                        onClick: () => setPreview(null),
                        style: { background: 'none', border: 'none', cursor: 'pointer', color: t.text.secondary }
                    }, 'Close')
                ),
                preview.opportunities.length
                    ? React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 } },
                        preview.opportunities.map(op => React.createElement(OpportunityCard, { key: op.id, op: op }))
                    )
                    : React.createElement('div', { style: { ...cardStyle, color: t.text.secondary } }, 'No current matches for this search.')
            ) : null,
            React.createElement('h2', { style: { fontFamily: FONT.display, fontSize: 20, margin: '0 0 12px', color: t.text.primary } },
                'Recent alert deliveries'
            ),
            s.alertHistory.length ? React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 10 } },
                s.alertHistory.map(alert => {
                    const statusMeta = alertStatusMeta(alert.deliveryStatus);
                    const isUnread = !alert.readAt;
                    return React.createElement('div', {
                        key: alert.id,
                        style: {
                            ...cardStyle,
                            opacity: isUnread ? 1 : 0.92,
                            borderColor: isUnread ? `${OC.orange}55` : t.border.base,
                        }
                    },
                        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' } },
                            React.createElement('div', { style: { flex: 1 } },
                                React.createElement('div', { style: { fontWeight: isUnread ? 700 : 600, color: t.text.primary } }, alert.title),
                                React.createElement('div', { style: { fontSize: 13, color: t.text.secondary, marginTop: 4 } },
                                    [alert.organization, alert.savedSearchName ? `via ${alert.savedSearchName}` : null, alert.sentAt ? new Date(alert.sentAt).toLocaleString() : null]
                                        .filter(Boolean).join(' · ')
                                ),
                                alert.errorMessage ? React.createElement('div', { style: { fontSize: 12, color: OC.urgent, marginTop: 6 } }, alert.errorMessage) : null
                            ),
                            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 } },
                                React.createElement('span', {
                                    style: {
                                        fontSize: 11,
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.04em',
                                        color: statusMeta.color,
                                        background: `${statusMeta.color}18`,
                                        padding: '4px 8px',
                                        borderRadius: 999,
                                    }
                                }, statusMeta.label),
                                isUnread ? React.createElement(Button, {
                                    kind: 'ghost', size: 'sm', theme: s.theme,
                                    onClick: () => s.markAlertRead(alert.id),
                                }, 'Mark read') : React.createElement('span', { style: { fontSize: 11, color: t.text.secondary } }, 'Read')
                            )
                        ),
                        alert.publicId ? React.createElement('button', {
                            onClick: () => s.go('detail', { id: alert.publicId }),
                            style: { marginTop: 10, background: 'none', border: 'none', color: OC.orange, cursor: 'pointer', fontWeight: 600, padding: 0 }
                        }, 'View opportunity') : null
                    );
                })
            ) : React.createElement('div', { style: { ...cardStyle, color: t.text.secondary } },
                'No alerts sent yet. Alerts run hourly when new jobs match your saved searches.'
            )
        )
    );
}

// --- UnsubscribeAlertScreen (public email link landing) ---
function UnsubscribeAlertScreen() {
    const s = useStore();
    const t = useTheme(s.theme);
    const [state, setState] = useState({ loading: true, error: '', searchName: '' });

    useEffect(() => {
        const token = new URLSearchParams(window.location.search).get('token');
        if (!token) {
            setState({ loading: false, error: 'Missing unsubscribe token.', searchName: '' });
            return;
        }
        (async () => {
            try {
                const data = await OCApi.unsubscribeAlertByToken(token);
                setState({
                    loading: false,
                    error: '',
                    searchName: data.savedSearch?.name || 'Saved search',
                });
            } catch (err) {
                setState({ loading: false, error: err.message || 'Could not unsubscribe.', searchName: '' });
            }
        })();
    }, []);

    return React.createElement('div', { style: { maxWidth: 560, margin: '0 auto', padding: '64px 32px 80px', width: '100%' } },
        React.createElement('h1', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 30, margin: '0 0 10px', color: t.text.primary } },
            'Email alert preferences'
        ),
        state.loading
            ? React.createElement('p', { style: { color: t.text.secondary } }, 'Updating your alert settings…')
            : state.error
                ? React.createElement('div', null,
                    React.createElement('p', { style: { color: OC.urgent, marginBottom: 16 } }, state.error),
                    React.createElement(Button, { kind: 'secondary', size: 'md', theme: s.theme, onClick: () => s.go('home') }, 'Back to home')
                )
                : React.createElement('div', null,
                    React.createElement('p', { style: { color: t.text.secondary, marginBottom: 16, lineHeight: 1.6 } },
                        `Email alerts are now off for "${state.searchName}". You can re-enable alerts anytime from your Alerts page.`
                    ),
                    React.createElement(Button, { kind: 'primary', size: 'md', theme: s.theme, onClick: () => s.go('notifications') }, 'Manage alerts')
                )
    );
}

export {
  AuthScreen, SearchScreen, SavedScreen, AlertsScreen, UnsubscribeAlertScreen,
};
